import Link from 'next/link'

// The pieces every overview page is built from (2026-11-01, the concept Sam
// approved): Today, Storylines and Topics are one page shape, no tabs.
//
//   OverviewHead   a section's heading: the kind's symbol, the name, one
//                  line saying what it is, and its controls at the right
//   PartHead       the small capitals over each part ("Most covered")
//   SideColumns    "The left covered more" | "The right covered more", side
//                  by side (stacked on a phone), with the rule under them
//   CompactRow     "everything else": one line a row, rank, title, a short
//                  context, the mark and the count
//
// The symbols name the three kinds and appear only in headings and kind
// tags, never in rows (Sam: "it feels repetitive" inline).

export function KindGlyph({ kind, className = '', large = false }) {
  if (kind === 'storyline' && large)
    return (
      <svg
        width="72"
        height="28"
        viewBox="0 0 72 28"
        aria-hidden="true"
        className={className}
      >
        <rect
          x="2"
          y="4"
          width="18"
          height="20"
          rx="4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
        />
        <rect
          x="27"
          y="4"
          width="18"
          height="20"
          rx="4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
        />
        <rect
          x="52"
          y="4"
          width="18"
          height="20"
          rx="4"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="2.4"
        />
      </svg>
    )
  if (kind === 'topic' && large)
    return (
      <svg
        width="30"
        height="30"
        viewBox="0 0 30 30"
        aria-hidden="true"
        className={className}
      >
        <circle
          cx="15"
          cy="15"
          r="12.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
        />
      </svg>
    )
  if (kind === 'storyline')
    return (
      <svg
        width="40"
        height="16"
        viewBox="0 0 40 16"
        aria-hidden="true"
        className={className}
      >
        <rect
          x="1"
          y="3"
          width="10"
          height="10"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <rect
          x="15"
          y="3"
          width="10"
          height="10"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <rect
          x="29"
          y="3"
          width="10"
          height="10"
          rx="2"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    )
  if (kind === 'topic')
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        aria-hidden="true"
        className={className}
      >
        <circle
          cx="9"
          cy="9"
          r="7.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    )
  return (
    <svg
      width="28"
      height="16"
      viewBox="0 0 28 16"
      aria-hidden="true"
      className={className}
    >
      <rect
        x="1.5"
        y="1.5"
        width="25"
        height="13"
        rx="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  )
}

export function OverviewHead({ kind, title, subtitle, aside, as = 'h2' }) {
  const H = as
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 border-b-[3px] border-foreground pb-3">
      <div className="min-w-0">
        <H className="flex items-center gap-3 font-serif text-2xl font-medium leading-tight text-foreground sm:text-[28px]">
          {kind && <KindGlyph kind={kind} className="shrink-0" />}
          {title}
        </H>
        {subtitle && (
          <p className="mt-1 font-sans text-[13px] text-muted-foreground">
            {subtitle}
          </p>
        )}
      </div>
      {aside && (
        <div className="flex flex-wrap items-center gap-3.5">{aside}</div>
      )}
    </div>
  )
}

// "All storylines", at the right of a section heading.
export function HeadLink({ href, children }) {
  return (
    <Link
      href={href}
      className="font-sans text-[13px] text-foreground underline underline-offset-[3px] hover:decoration-2"
    >
      {children}
    </Link>
  )
}

export function PartHead({ children, aside, className = '' }) {
  return (
    <div
      className={`flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-foreground py-2.5 ${className}`}
    >
      <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
        {children}
      </h3>
      {aside && (
        <span className="font-sans text-xs text-muted-foreground">{aside}</span>
      )}
    </div>
  )
}

// The colour key, once per page, on the first part's line.
export function LeanKey() {
  const sw = 'mr-1.5 inline-block h-2 w-3 rounded-[2px] align-[1px]'
  return (
    <span className="hidden flex-wrap gap-x-3.5 sm:flex">
      <span>
        <span className={`${sw} bg-blue-600`} aria-hidden="true" />
        Left-leaning
      </span>
      <span>
        <span className={`${sw} bg-zinc-500`} aria-hidden="true" />
        Center
      </span>
      <span>
        <span className={`${sw} bg-red-600`} aria-hidden="true" />
        Right-leaning outlets
      </span>
    </span>
  )
}

const SIDE = {
  left: { title: 'The left covered more', rule: 'border-t-blue-600' },
  right: { title: 'The right covered more', rule: 'border-t-red-600' },
}

/**
 * The two side lists. `left` and `right` are rendered rows (ListRow narrow);
 * `subtitle(side)` says what qualifies ("Stories Pano rates Leans or Mostly
 * Left"); `empty(side)` is the line shown when nothing does; `rule` is the
 * sentence under both saying how they are ranked.
 */
export function SideColumns({ left, right, subtitle, empty, rule }) {
  const column = (side, rows) => (
    <div className="min-w-0">
      <div
        className={`border-b border-t-4 border-b-foreground pb-2.5 pt-3 ${SIDE[side].rule}`}
      >
        <h3 className="font-serif text-[22px] font-medium leading-tight text-foreground">
          {SIDE[side].title}
        </h3>
        {subtitle && (
          <p className="mt-0.5 font-sans text-xs text-muted-foreground">
            {subtitle(side)}
          </p>
        )}
      </div>
      {rows.length ? (
        <ol>{rows}</ol>
      ) : (
        <p className="border-b border-border py-5 font-sans text-sm text-muted-foreground">
          {empty(side)}
        </p>
      )}
    </div>
  )
  return (
    <div>
      <div className="grid gap-10 md:grid-cols-2 md:gap-12">
        {column('left', left)}
        {column('right', right)}
      </div>
      {rule && (
        <p className="mt-3.5 font-sans text-xs leading-relaxed text-muted-foreground">
          {rule}
        </p>
      )}
    </div>
  )
}

/**
 * One line of "everything else". mark: the row's coverage mark (desktop
 * only); sub: a short context after the title (desktop only, it truncates).
 * `children` replaces the title link when the caller needs a tracked link.
 */
export function CompactRow({ rank, href, title, sub, mark, figure, children }) {
  return (
    <li className="relative grid grid-cols-[1.5rem_minmax(0,1fr)_2.5rem] items-center gap-x-3 border-b border-border py-2 sm:grid-cols-[2.5rem_minmax(0,1fr)_200px_4.5rem] sm:gap-x-4">
      <span className="font-serif text-base tabular-nums text-muted-foreground">
        {rank}
      </span>
      <span className="flex min-w-0 items-baseline gap-2.5">
        {children || (
          <Link
            href={href}
            className={`min-w-0 truncate font-serif text-[17px] leading-snug text-foreground after:absolute after:inset-0 after:content-[''] hover:underline ${sub ? 'sm:max-w-[70%] sm:shrink-0' : ''}`}
          >
            {title}
          </Link>
        )}
        {sub && (
          <span className="hidden min-w-0 truncate font-sans text-xs text-muted-foreground sm:inline">
            {sub}
          </span>
        )}
      </span>
      <span className="hidden sm:block">{mark}</span>
      <span className="text-right font-serif text-base tabular-nums text-foreground">
        {figure}
      </span>
    </li>
  )
}

// "Show 25 more", the overview pages' way past the top rows.
export function MoreButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-4 font-sans text-sm font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2"
    >
      {children}
    </button>
  )
}

// "▲ 14× a usual week": a row running far above its normal (the old
// Trending tab, as a mark on any storyline or topic row). `text` is the
// rising rule's own wording (utils/coverageTrends.risingLabel).
export function RisingMark({ text, className = '' }) {
  if (!text) return null
  return (
    <span
      className={`font-sans text-xs font-semibold text-foreground ${className}`}
    >
      ▲ {text}
    </span>
  )
}
